import {
  GroupShapeIcon,
  type GroupShape,
} from "@/components/shapes/GroupShapeIcon";
import type { StaticLayerKey } from "@/configs/map/layers";
import { useProcessedData } from "@/stores/data-processing";
import { useMapStore } from "@/stores/map";
import type { WorkspaceModalMode } from "@/types/stores/ui";
import {
  GROUP_PREFIX,
  PROCESSED_LAYER_PREFIX,
  TREE_NODE_IDS,
} from "@/utils/constants/map";
import {
  ActionIcon,
  Checkbox,
  Flex,
  Group,
  Select,
  Text,
  Tooltip,
  type RenderTreeNodePayload,
} from "@mantine/core";
import {
  IconBuildingCommunity,
  IconChevronDown,
  IconClock,
  IconFolder,
  IconFolderOpen,
  IconGrain,
  IconPencil,
  IconTrash,
  IconWorld,
} from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import type { ProcessedGroup } from "../hooks/useProcessedGroups";
import type { LayerTreeNodeData } from "../types";
import "./LayerTreeNode.css";

interface LayerTreeNodeProps {
  payload: RenderTreeNodePayload;
  staticLayers: Record<StaticLayerKey, boolean>;
  geoJSONLayers: Record<string, { isVisible?: boolean }>;
  visibleGroups: string[];
  processedGroups: ProcessedGroup[];
  groupColumnName: string | null;
  columnOptions: Array<{
    value: string;
    label: string;
    uniqueCount: number;
    disabled?: boolean;
  }>;
  inputDataName: string | null;
  activeDataflowId: string | null;
  setGroupColumnName: (
    dataflowId: string,
    columnName: string | null,
    uniqueValues?: string[]
  ) => void;
  setVisibleGroups: (dataflowId: string, groups: string[]) => void;
  updateLayerVisibility: (layerId: string, visible: boolean) => void;
  getGroupConfig: (
    dataflowId: string,
    groupValue: string
  ) =>
    | {
        name?: string;
        color?: string;
        shape?: "circle" | "square" | "triangle" | "star" | "diamond";
      }
    | undefined;
  openModal: (
    dataflowId: string | undefined,
    mode: WorkspaceModalMode,
    title?: string,
    data?: unknown
  ) => void;
}

function LayerTreeNodeComponent({
  payload,
  staticLayers: _staticLayers,
  geoJSONLayers: _geoJSONLayers,
  visibleGroups: _visibleGroups,
  processedGroups,
  groupColumnName,
  columnOptions,
  inputDataName,
  activeDataflowId,
  setGroupColumnName,
  setVisibleGroups: _setVisibleGroups,
  updateLayerVisibility,
  getGroupConfig: _getGroupConfig,
  openModal,
}: LayerTreeNodeProps) {
  const { node, expanded, hasChildren, elementProps, tree } = payload;
  const extendedNode = node as LayerTreeNodeData;
  const isFolder = extendedNode.type === "folder";
  const isGroup = extendedNode.type === "group";
  const isChunk = extendedNode.type === "chunk";
  const isVisualization = extendedNode.type === "visualization";
  const processedData = useProcessedData(activeDataflowId || "");

  // Get full visualization mode info (mode + layerType)
  // Use useShallow to avoid creating new objects on each call
  const visualizationModeInfo = useMapStore(
    useShallow((state) => {
      if (activeDataflowId) {
        const groupState = state.groups[activeDataflowId];
        if (!groupState) {
          return { mode: "points" as const, layerType: null as null };
        }
        return {
          mode: (groupState.visualizationMode || "points") as
            | "points"
            | "layers",
          layerType: (groupState.visualizationLayerType || null) as
            | "municipalities"
            | "countries"
            | "timezones"
            | null,
        };
      }
      return { mode: "points" as const, layerType: null as null };
    })
  );

  // Get setVisualizationMode action
  const setVisualizationMode = useMapStore(
    (state) => state.setVisualizationMode
  );

  // Build visualization mode options with icons
  const visualizationOptions = useMemo(() => {
    return [
      {
        group: "Point layers",
        items: [
          {
            value: "points-cluster",
            label: "Clusters",
            icon: IconGrain,
          },
        ],
      },
      {
        group: "Brick layers",
        items: [
          {
            value: "layers-municipalities",
            label: "Municipalities",
            icon: IconBuildingCommunity,
          },
          {
            value: "layers-countries",
            label: "Countries",
            icon: IconWorld,
          },
          {
            value: "layers-timezones",
            label: "Timezones",
            icon: IconClock,
          },
        ],
      },
    ];
  }, []);

  // Create a map of value to icon for easy lookup
  const visualizationIconMap = useMemo(() => {
    const map = new Map();
    visualizationOptions.forEach((group) => {
      group.items.forEach((item) => {
        if (item.icon) {
          map.set(item.value, item.icon);
        }
      });
    });
    return map;
  }, [visualizationOptions]);

  // Get current visualization mode value for dropdown
  const currentVisualizationValue = useMemo(() => {
    if (!visualizationModeInfo) {
      return "points-cluster";
    }

    if (visualizationModeInfo.mode === "points") {
      return "points-cluster";
    } else if (
      visualizationModeInfo.mode === "layers" &&
      visualizationModeInfo.layerType
    ) {
      return `layers-${visualizationModeInfo.layerType}`;
    }

    return "points-cluster";
  }, [visualizationModeInfo]);

  // Handle visualization mode change
  const onVisualizationModeChange = useCallback(
    (value: string | null) => {
      if (!activeDataflowId || !value) return;

      const [mode, subOption] = value.split("-");
      if (mode === "points") {
        setVisualizationMode(activeDataflowId, "points", null);
      } else if (mode === "layers") {
        const layerType = subOption as
          | "municipalities"
          | "countries"
          | "timezones";
        setVisualizationMode(activeDataflowId, "layers", layerType);
      }
    },
    [activeDataflowId, setVisualizationMode]
  );

  // Determine layer ID for this node
  const layerId = useMemo(() => {
    if (extendedNode.type === "layer") {
      if (node.value === TREE_NODE_IDS.MY_DATA_INPUT) {
        if (activeDataflowId) {
          return `data:${PROCESSED_LAYER_PREFIX}${activeDataflowId}`;
        }
        return null;
      } else if (node.value.startsWith(GROUP_PREFIX)) {
        // Legacy group format - convert to unified ID
        const groupValue = node.value.replace(GROUP_PREFIX, "");
        return `group:${activeDataflowId}:${groupValue}`;
      } else if (
        activeDataflowId &&
        processedGroups.some((g) => g.id === node.value && g.type === "group")
      ) {
        // Group from processedGroups (unified format)
        return `group:${activeDataflowId}:${node.value}`;
      } else {
        // Static layer
        return `static:${node.value}`;
      }
    } else if (isChunk || extendedNode.type === "chunk") {
      // Chunk - use unified ID
      return `chunk:${activeDataflowId}:${node.value}`;
    } else if (isGroup && node.value === TREE_NODE_IDS.MY_DATA_INPUT_GROUP) {
      // Column selector node - doesn't have its own layer ID, but we'll handle it specially
      return null;
    }
    return null;
  }, [
    extendedNode.type,
    node.value,
    activeDataflowId,
    isChunk,
    isGroup,
    processedGroups,
  ]);

  // Track last selected column to restore when checking
  const lastSelectedColumnRef = useRef<string | null>(null);

  // Update ref when column changes
  useEffect(() => {
    if (groupColumnName) {
      lastSelectedColumnRef.current = groupColumnName;
    }
  }, [groupColumnName]);

  // Reactive checkbox state - subscribe to store changes
  const checked = useMapStore((state) => {
    // Column selector node - checked when a column is selected
    if (isGroup && node.value === TREE_NODE_IDS.MY_DATA_INPUT_GROUP) {
      return groupColumnName !== null;
    }
    if (isVisualization && node.value === TREE_NODE_IDS.VISUALIZATION_MODE) {
      return false;
    }

    if (!layerId) {
      // For MY_DATA_INPUT without activeDataflowId, default to visible
      if (node.value === TREE_NODE_IDS.MY_DATA_INPUT) {
        return true;
      }
      return false;
    }

    // Check unified visibility map first
    if (layerId in state.layerVisibility) {
      return state.layerVisibility[layerId];
    }

    // Fall back to legacy maps for backward compatibility
    if (layerId.startsWith("static:")) {
      const staticLayerId = layerId.replace("static:", "");
      return state.staticLayers[staticLayerId] ?? false;
    } else if (layerId.startsWith("data:")) {
      const dataLayerId = layerId.replace("data:", "");
      const dataLayerVisible = state.dataLayers[dataLayerId];
      const geoJSONLayer = state.geoJSONLayers[dataLayerId];
      return dataLayerVisible ?? geoJSONLayer?.isVisible ?? true;
    } else if (layerId.startsWith("group:") || layerId.startsWith("chunk:")) {
      const parts = layerId.split(":");
      if (parts.length >= 3) {
        const workspaceId = parts[1];
        const groupId = parts.slice(2).join(":");
        const groupState = state.groups[workspaceId];
        if (layerId.startsWith("chunk:")) {
          // For chunks, check if all values are visible
          const chunk = groupState?.chunks?.find((c) => c.value === groupId);
          if (chunk) {
            return (
              chunk.values.every((value) =>
                groupState?.visibleGroups?.includes(value)
              ) ?? false
            );
          }
        } else {
          return groupState?.visibleGroups?.includes(groupId) ?? false;
        }
      }
    }

    return false;
  });

  const indeterminate = tree.isNodeIndeterminate(node.value);

  // Get shape for chunks and groups - subscribe reactively to configs changes
  const chunkShape = useMapStore((state) => {
    if (isChunk && activeDataflowId) {
      const chunkConfig =
        state.groups[activeDataflowId]?.chunkConfigs?.[node.value];
      return (chunkConfig?.shape || "circle") as GroupShape;
    }
    return "circle" as GroupShape;
  });

  // Check if this is a group (non-chunked) from processedGroups
  const isProcessedGroup = useMemo(() => {
    if (!activeDataflowId || !processedGroups) return false;
    return processedGroups.some(
      (g) => g.id === node.value && g.type === "group"
    );
  }, [activeDataflowId, processedGroups, node.value]);

  // Get group shape for non-chunked groups - subscribe reactively to groupConfigs changes
  // Get the processed group to access its shape
  const processedGroup = useMemo(() => {
    if (!activeDataflowId || !processedGroups) return null;
    return processedGroups.find(
      (g) => g.id === node.value && g.type === "group"
    );
  }, [activeDataflowId, processedGroups, node.value]);

  const groupShape = useMapStore((state) => {
    if (isProcessedGroup && activeDataflowId) {
      const groupConfig =
        state.groups[activeDataflowId]?.groupConfigs?.[node.value];
      // Get shape from config, or from processedGroup, or default to circle
      return (groupConfig?.shape ||
        processedGroup?.shape ||
        "circle") as GroupShape;
    }
    return "circle" as GroupShape;
  });

  const onToggleExpanded = useCallback(() => {
    tree.toggleExpanded(node.value);
  }, [tree, node.value]);

  const getLayerRegionConfig = useMapStore(
    (state) => state.getLayerRegionConfig
  );

  // Simplified checkbox click handler using unified layer visibility
  const onCheckboxClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();

      if (!activeDataflowId) return;

      // Handle column selector node checkbox
      if (isGroup && node.value === TREE_NODE_IDS.MY_DATA_INPUT_GROUP) {
        const currentChecked = groupColumnName !== null;
        if (currentChecked) {
          // Unchecking: deselect the column
          setGroupColumnName(activeDataflowId, null);
          // Collapse the node when unchecked
          setTimeout(() => {
            tree.collapse(node.value);
          }, 0);
        } else {
          // Checking: restore the last selected column if available
          if (lastSelectedColumnRef.current) {
            setGroupColumnName(activeDataflowId, lastSelectedColumnRef.current);
            // Expand the node when checked
            setTimeout(() => {
              tree.expand(node.value);
              tree.expand(TREE_NODE_IDS.MY_DATA);
            }, 0);
          }
        }
        return;
      }

      // Determine layer ID first
      let layerId: string | null = null;
      if (isChunk || extendedNode.type === "chunk") {
        layerId = `chunk:${activeDataflowId}:${node.value}`;
      } else if (
        extendedNode.type === "layer" &&
        node.value.startsWith(GROUP_PREFIX)
      ) {
        // Legacy group format with GROUP_PREFIX
        const groupValue = node.value.replace(GROUP_PREFIX, "");
        layerId = `group:${activeDataflowId}:${groupValue}`;
      } else if (
        extendedNode.type === "layer" &&
        node.value === TREE_NODE_IDS.MY_DATA_INPUT
      ) {
        layerId = `data:${PROCESSED_LAYER_PREFIX}${activeDataflowId}`;
      } else if (
        extendedNode.type === "layer" &&
        activeDataflowId &&
        processedGroups.some((g) => g.id === node.value && g.type === "group")
      ) {
        // Group from processedGroups (unified format)
        layerId = `group:${activeDataflowId}:${node.value}`;
      } else if (extendedNode.type === "layer") {
        layerId = `static:${node.value}`;
      }

      // Get current visibility state
      const currentVisible = layerId
        ? useMapStore.getState().getLayerVisibilityById(layerId)
        : false;
      const newVisible = !currentVisible;

      // Update visibility - use unified layer visibility for all nodes
      if (layerId) {
        updateLayerVisibility(layerId, newVisible);
      }

      // Don't call onClick handler if we already handled it via updateLayerVisibility
      // The onClick handler in treeData is redundant and causes double-toggling

      // Expand/collapse data layer node based on visibility state
      if (node.value === TREE_NODE_IDS.MY_DATA_INPUT) {
        // Use the same pattern as column selector - setTimeout with 0 delay
        // Also ensure parent folder is expanded when expanding
        setTimeout(() => {
          if (newVisible) {
            tree.expand(TREE_NODE_IDS.MY_DATA);
            tree.expand(node.value);
          } else {
            tree.collapse(node.value);
          }
        }, 0);
      }
    },
    [
      extendedNode,
      node.value,
      isChunk,
      isGroup,
      activeDataflowId,
      updateLayerVisibility,
      hasChildren,
      tree,
      processedGroups,
      checked,
      groupColumnName,
      setGroupColumnName,
    ]
  );

  const onGroupChange = useCallback(
    (value: string | null) => {
      if (!activeDataflowId) {
        return;
      }

      // Prevent any default behavior
      // Always expand when changing column to ensure the node stays visible

      let uniqueValuesForChunks: string[] | undefined;
      if (value && processedData?.data) {
        const uniqueValuesSet = new Set<string>();
        processedData.data.forEach((row) => {
          const groupValue = row[value];
          if (groupValue != null && groupValue !== "") {
            const group = String(groupValue).trim();
            uniqueValuesSet.add(group);
          }
        });
        uniqueValuesForChunks = Array.from(uniqueValuesSet).sort();
      }

      // setGroupColumnName will handle setting visibleGroups when uniqueValues are provided
      setGroupColumnName(activeDataflowId, value, uniqueValuesForChunks);

      // Expand/collapse nodes based on whether a column is selected
      // Use setTimeout to ensure this runs after any tree re-render
      if (isGroup && node.value === TREE_NODE_IDS.MY_DATA_INPUT_GROUP) {
        setTimeout(() => {
          if (value) {
            // Column selected: expand the node
            tree.expand(node.value);
            tree.expand(TREE_NODE_IDS.MY_DATA);
          } else {
            // Column deselected: collapse the node
            tree.collapse(node.value);
          }
        }, 0);
      }
    },
    [
      activeDataflowId,
      processedData,
      setGroupColumnName,
      isGroup,
      node.value,
      tree,
    ]
  );

  const onEditItem = useCallback(
    (e: React.MouseEvent, data: LayerTreeNodeData) => {
      e.preventDefault();
      e.stopPropagation();
      // Check if it's a layer region (value starts with "layer-")
      if (data.value?.startsWith("layer-")) {
        return onEditLayerRegion(data);
      }
      switch (data.type) {
        case "chunk":
          return onEditChunk(data);
        case "layer":
          return onEditLayer(data);
        case "folder":
          return onEditFolder(data);
        case "visualization":
          // Visualization mode - open visualization modal
          return onEditVisualization(data);
        case "group":
          return onEditGroup(data);
        default:
          break;
      }
    },
    []
  );

  const onEditLayerRegion = useCallback(
    (data: LayerTreeNodeData) => {
      if (!activeDataflowId || !data.value) {
        return;
      }

      // Layer regions are not in processedGroups, they're handled separately
      // Just get the config directly
      const currentConfig = getLayerRegionConfig(activeDataflowId, data.value);

      // Extract name from label (remove point count)
      const labelStr =
        typeof data.label === "string" ? data.label : String(data.label || "");
      const labelMatch = labelStr.match(/^(.+?)\s*\(/);
      const regionName = labelMatch ? labelMatch[1] : labelStr;

      openModal(
        activeDataflowId ?? undefined,
        "map-cluster",
        "Edit Layer Region",
        {
          chunkValue: data.value,
          initialDisplayName: currentConfig?.displayName || regionName || "",
          initialFrom: regionName,
          initialTo: regionName,
          initialColor: currentConfig?.color || "#003399",
          initialShape: "circle",
          initialVisible: currentConfig?.isVisible ?? true,
          isLayerRegion: true,
        }
      );
    },
    [activeDataflowId, getLayerRegionConfig, openModal]
  );

  const onEditChunk = useCallback(
    (data: LayerTreeNodeData) => {
      if (!activeDataflowId || !data.value) {
        return;
      }

      // Get fresh state from store when opening modal
      const state = useMapStore.getState();
      const currentConfig = state.getChunkConfig(activeDataflowId, data.value);

      // Get unified groups to find the chunk
      const unifiedGroups = state.unifiedGroups[activeDataflowId];
      const chunk = unifiedGroups?.groups.find(
        (g) => g.id === data.value && g.type === "chunk"
      );

      const extractFromTo = (label: string) => {
        const match = label.match(/^(.+?)\s*-\s*(.+)$/);
        if (match) {
          return { from: match[1], to: match[2] };
        }
        return { from: label, to: label };
      };

      const fromTo = chunk?.label
        ? extractFromTo(chunk.label)
        : chunk?.from && chunk?.to
        ? { from: chunk.from, to: chunk.to }
        : { from: "", to: "" };

      // Check visibility from unified groups or visibleGroups
      const visibleGroups = state.groups[activeDataflowId]?.visibleGroups || [];
      const isVisible = chunk
        ? chunk.values.every((v) => visibleGroups.includes(v))
        : true;

      openModal(activeDataflowId ?? undefined, "map-cluster", "Edit Cluster", {
        chunkValue: data.value,
        initialDisplayName:
          currentConfig?.displayName ||
          chunk?.displayName ||
          chunk?.label ||
          "",
        initialFrom: currentConfig?.from || chunk?.from || fromTo.from,
        initialTo: currentConfig?.to || chunk?.to || fromTo.to,
        initialColor: currentConfig?.color || chunk?.color || "#003399",
        initialShape: currentConfig?.shape || chunk?.shape || "circle",
        initialVisible: isVisible,
      });
    },
    [activeDataflowId, openModal]
  );

  const onEditData = useCallback(
    (_data: LayerTreeNodeData) => {
      if (!activeDataflowId) {
        return;
      }

      // Get fresh state from store when opening modal
      const state = useMapStore.getState();
      const geoJSONLayerId = `${PROCESSED_LAYER_PREFIX}${activeDataflowId}`;
      const geoJSONLayer = state.geoJSONLayers[geoJSONLayerId];
      const isVisible = geoJSONLayer?.isVisible ?? true;

      // Get layer config if no groups/clusters are set
      const layerConfig = state.getLayerConfig(activeDataflowId);
      const currentGroupColumnName =
        state.groups[activeDataflowId]?.groupColumnName ?? null;

      openModal(activeDataflowId ?? undefined, "map-data", "Edit Data Input", {
        initialName: inputDataName,
        initialVisible: isVisible,
        initialColor: layerConfig?.color || "#003399",
        initialShape: layerConfig?.shape || "circle",
        hasGroupsOrClusters: currentGroupColumnName !== null,
      });
    },
    [activeDataflowId, inputDataName, openModal]
  );

  const onEditLayer = useCallback(
    (data: LayerTreeNodeData) => {
      if (!activeDataflowId) {
        return;
      }

      if (data.value === TREE_NODE_IDS.MY_DATA_INPUT) {
        return onEditData(data);
      }

      // Check if this is a group from processedGroups (unified format)
      const isUnifiedGroup = processedGroups.some(
        (g) => g.id === data.value && g.type === "group"
      );

      // Check if this is a legacy group with GROUP_PREFIX
      const isLegacyGroup = data.value.startsWith(GROUP_PREFIX);

      if (isUnifiedGroup || isLegacyGroup) {
        const groupValue = isLegacyGroup
          ? data.value.replace(GROUP_PREFIX, "")
          : data.value;

        // Get fresh state from store when opening modal
        const state = useMapStore.getState();
        const currentConfig = state.getGroupConfig(
          activeDataflowId,
          groupValue
        );

        // Also check unified groups for latest color/shape
        const unifiedGroups = state.unifiedGroups[activeDataflowId];
        const unifiedGroup = unifiedGroups?.groups.find(
          (g) => g.id === groupValue && g.type === "group"
        );

        // Use group-config modal for groups (calls setGroupConfig)
        openModal(activeDataflowId ?? undefined, "group-config", "Edit Group", {
          groupValue,
          initialName:
            currentConfig?.name ||
            unifiedGroup?.displayName ||
            unifiedGroup?.label ||
            data.label ||
            groupValue,
          initialColor:
            currentConfig?.color ||
            unifiedGroup?.color ||
            data.color ||
            "#003399",
          initialShape:
            currentConfig?.shape ||
            unifiedGroup?.shape ||
            (data as { shape?: GroupShape }).shape ||
            "circle",
        });
      }
    },
    [activeDataflowId, openModal, onEditData, processedGroups]
  );

  const onEditFolder = useCallback(
    (data: LayerTreeNodeData) => {
      if (!activeDataflowId || !data.value) {
        return;
      }

      const getFolderName = useMapStore.getState().getFolderName;
      const currentName = getFolderName(activeDataflowId, data.value);

      let defaultName = String(data.label || "");
      if (data.value === TREE_NODE_IDS.STATIC_LAYERS) {
        defaultName = "Static Layers";
      } else if (data.value === TREE_NODE_IDS.MY_DATA) {
        defaultName = "My data";
      }

      openModal(activeDataflowId ?? undefined, "map-folder", "Edit Folder", {
        folderId: data.value,
        initialName: currentName || defaultName,
      });
    },
    [activeDataflowId, openModal]
  );

  const onEditGroup = useCallback(
    (_data: LayerTreeNodeData) => {
      if (!activeDataflowId) {
        return;
      }

      openModal(activeDataflowId ?? undefined, "map-group", "Edit Group", {
        initialColumnName: groupColumnName,
      });
    },
    [activeDataflowId, groupColumnName, openModal]
  );

  const onEditVisualization = useCallback(
    (_data: LayerTreeNodeData) => {
      if (!activeDataflowId) {
        return;
      }

      openModal(
        activeDataflowId ?? undefined,
        "map-visualization",
        "Edit Visualization Mode"
      );
    },
    [activeDataflowId, openModal]
  );

  let { onClick: expandCollapseHandler, ...nodeElementProps } =
    elementProps || {};

  const itemClassName = `item ${
    isFolder || isChunk ? "item-folder" : "item-layer"
  } ${expanded ? "expanded" : ""}`;

  const FolderIcon = expanded ? IconFolderOpen : IconFolder;

  // Get the icon component for the current visualization mode
  const VisualizationIcon = useMemo(() => {
    return visualizationIconMap.get(currentVisualizationValue) || IconGrain;
  }, [currentVisualizationValue, visualizationIconMap]);

  return (
    <Group
      justify="space-between"
      px={6}
      py={0}
      gap={6}
      {...nodeElementProps}
      className={itemClassName}
      onClick={(e) => {
        // Don't handle clicks on the Select component, checkbox, or action buttons
        // These should not trigger expansion/collapse
        if (
          (e.target as HTMLElement).closest(
            ".mantine-Select-root, .mantine-Select-input, .mantine-Checkbox-root, .item-actions"
          )
        ) {
          e.stopPropagation();
          return;
        }
        // For folders, always allow expansion/collapse
        if (isFolder && expandCollapseHandler) {
          expandCollapseHandler(e);
          return;
        }
        // Use default expansion handler from Mantine Tree
        if (expandCollapseHandler) {
          expandCollapseHandler(e);
        } else if (hasChildren) {
          // Fallback: manually toggle expansion
          onToggleExpanded();
        }
      }}
    >
      <Flex align="center" gap={6} flex={1}>
        {isChunk ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onCheckboxClick(e);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              opacity: checked ? 1 : indeterminate ? 0.5 : 0.3,
            }}
          >
            <GroupShapeIcon
              shape={chunkShape}
              color={extendedNode.color || "#003399"}
              size={16}
              filled={checked || indeterminate}
            />
          </div>
        ) : isProcessedGroup ? (
          // Non-chunked group - show shape and color icon like chunks
          <div
            onClick={(e) => {
              e.stopPropagation();
              onCheckboxClick(e);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              opacity: checked ? 1 : indeterminate ? 0.5 : 0.3,
            }}
          >
            <GroupShapeIcon
              shape={groupShape}
              color={extendedNode.color || "#003399"}
              size={16}
              filled={checked || indeterminate}
            />
          </div>
        ) : isFolder ? (
          <FolderIcon size={16} stroke={1.4} />
        ) : isGroup ? (
          // Column selector node - show checkbox
          <Checkbox.Indicator
            checked={checked}
            indeterminate={false}
            size="xs"
            onClick={onCheckboxClick}
          />
        ) : isVisualization ? (
          // Visualization mode node - show icon matching current selection
          <VisualizationIcon size={16} stroke={1.4} />
        ) : (
          <Checkbox.Indicator
            checked={checked}
            indeterminate={indeterminate}
            size="xs"
            color={extendedNode.color}
            onClick={onCheckboxClick}
          />
        )}
        {isGroup ? (
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ flex: 1, minWidth: 0 }}
          >
            <Select
              flex={1}
              variant="unstyled"
              checkIconPosition="right"
              data={columnOptions}
              value={groupColumnName ?? null}
              allowDeselect={true}
              onChange={onGroupChange}
              placeholder={String(extendedNode.label ?? "Select...")}
              styles={{
                input: {
                  fontSize: 15,
                },
              }}
              searchable={false}
            />
          </div>
        ) : isVisualization ? (
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ flex: 1, minWidth: 0 }}
          >
            <Select
              flex={1}
              variant="unstyled"
              checkIconPosition="right"
              data={visualizationOptions}
              value={currentVisualizationValue}
              onChange={onVisualizationModeChange}
              placeholder="Select visualization mode..."
              styles={{
                input: {
                  fontSize: 15,
                },
              }}
              searchable={false}
              renderOption={({ option }) => {
                const IconComponent = visualizationIconMap.get(option.value);
                return (
                  <Flex align="center" gap={8}>
                    {IconComponent && <IconComponent size={16} stroke={1.4} />}
                    <Text size="sm">{option.label}</Text>
                  </Flex>
                );
              }}
            />
          </div>
        ) : (
          <Text truncate flex={1} fz={15} className="item-label" miw={0}>
            {node.label}
          </Text>
        )}
      </Flex>
      {(isFolder ||
        isChunk ||
        isGroup ||
        isVisualization ||
        extendedNode.type === "layer") && (
        <Flex className="item-actions" gap={4}>
          <Tooltip
            label={
              isFolder
                ? "Edit Folder"
                : isChunk
                ? "Edit Cluster"
                : isGroup
                ? "Edit Group"
                : isVisualization
                ? "Edit Visualization Mode"
                : "Edit Layer"
            }
          >
            <ActionIcon
              onClick={(e) => onEditItem(e, extendedNode)}
              size="sm"
              variant="subtle"
              color="gray.6"
            >
              <IconPencil stroke={1.4} />
            </ActionIcon>
          </Tooltip>
          {isChunk && (
            <Tooltip label="Remove Cluster">
              <ActionIcon
                onClick={(e) => onEditItem(e, extendedNode)}
                size="sm"
                variant="subtle"
                color="gray.6"
              >
                <IconTrash stroke={1.4} />
              </ActionIcon>
            </Tooltip>
          )}
        </Flex>
      )}
      {hasChildren && (
        <Flex align="center" p={4} className="item-chevron-container">
          <IconChevronDown className="item-chevron" size={14} />
        </Flex>
      )}
    </Group>
  );
}

// Memoize component to prevent unnecessary re-renders
export const LayerTreeNode = memo(
  LayerTreeNodeComponent,
  (prevProps, nextProps) => {
    // Custom comparison function for better performance
    // Check if node data (including color) has changed
    const prevNode = prevProps.payload.node as LayerTreeNodeData;
    const nextNode = nextProps.payload.node as LayerTreeNodeData;
    const nodeDataChanged =
      prevNode.value !== nextNode.value ||
      prevNode.color !== nextNode.color ||
      prevNode.label !== nextNode.label;

    return (
      !nodeDataChanged &&
      prevProps.payload.expanded === nextProps.payload.expanded &&
      prevProps.staticLayers === nextProps.staticLayers &&
      prevProps.visibleGroups.length === nextProps.visibleGroups.length &&
      prevProps.visibleGroups.join(",") === nextProps.visibleGroups.join(",") &&
      prevProps.groupColumnName === nextProps.groupColumnName &&
      prevProps.activeDataflowId === nextProps.activeDataflowId &&
      prevProps.geoJSONLayers === nextProps.geoJSONLayers
    );
  }
);
